import React, { useState, useEffect } from 'react';
import './PujaListReadOnly.css';
import './PujaManagement.css';
import AddPujaForm from './AddPujaForm';
import API_CONFIG from '../../config/apiConfig';

const PujaManagement = () => {
  const [pujas, setPujas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPuja, setEditingPuja] = useState(null);
  const [selectedPuja, setSelectedPuja] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'

  useEffect(() => {
    fetchPujas();
  }, []);

  const fetchPujas = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.getBaseUrl()}/api/puja-list/list/`);

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setPujas(data);
        } else if (data && Array.isArray(data.results)) {
          setPujas(data.results);
        } else {
          setPujas([]);
        }
        setError(null);
      } else {
        setError('Failed to fetch pujas');
      }
    } catch (error) {
      console.error('Error fetching pujas:', error);
      setError('Failed to load pujas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPujaDetails = async (pujaId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.getBaseUrl()}/api/puja-list/${pujaId}/`);
      if (response.ok) {
        const data = await response.json();
        setSelectedPuja(data);
        setViewMode('detail');
        setError(null);
      } else {
        setError('Failed to fetch puja details');
      }
    } catch (err) {
      console.error('Error fetching puja details:', err);
      setError('Failed to fetch puja details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePujaClick = (puja) => {
    fetchPujaDetails(puja.id);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedPuja(null);
  };

  const handleDelete = async (e, pujaId) => {
    e.stopPropagation(); // Prevent card click
    if (!window.confirm('Are you sure you want to delete this puja?')) {
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.getBaseUrl()}/api/puja-list/delete/${pujaId}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Puja deleted successfully');
        await fetchPujas();
      } else {
        const errorText = await response.text();
        console.error('Delete error:', errorText);
        alert(`Failed to delete puja: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting puja:', error);
      alert('Error deleting puja');
    }
  };

  const handleSave = async (formData) => {
    try {
      const url = editingPuja
        ? `${API_CONFIG.getBaseUrl()}/api/puja-list/update/${editingPuja.id}/`
        : `${API_CONFIG.getBaseUrl()}/api/puja-list/add/`;

      const method = editingPuja ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowAddForm(false);
        setEditingPuja(null);
        alert(editingPuja ? 'Puja updated successfully' : 'Puja created successfully');
        await fetchPujas();
      } else {
        alert(editingPuja ? 'Failed to update puja' : 'Failed to create puja');
      }
    } catch (error) {
      console.error('Error saving puja:', error);
      alert('Error saving puja');
    }
  };

  const handleEdit = async (e, puja) => {
    e.stopPropagation(); // Prevent card click

    try {
      // Fetch full puja details including items
      const response = await fetch(`${API_CONFIG.getBaseUrl()}/api/puja-list/${puja.id}/`);
      if (response.ok) {
        const fullPujaData = await response.json();
        setEditingPuja(fullPujaData);
        setShowAddForm(true);
      } else {
        // Fallback to the puja data we have
        setEditingPuja(puja);
        setShowAddForm(true);
      }
    } catch (error) {
      console.error('Error fetching puja details for edit:', error);
      // Fallback to the puja data we have
      setEditingPuja(puja);
      setShowAddForm(true);
    }
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingPuja(null);
  };

  if (showAddForm) {
    return (
      <AddPujaForm
        onClose={handleCloseForm}
        onSave={handleSave}
        editingPuja={editingPuja}
      />
    );
  }

  if (loading) {
    return (
      <div className="puja-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading pujas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="puja-container">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchPujas} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="puja-container">
      {viewMode === 'list' ? (
        <>
          <div className="puja-header">
            <h2>🛕 Puja Management</h2>
            <p>Browse, add, edit, and manage pujas and their samagri</p>
            <div className="header-actions">
              <button onClick={fetchPujas} className="refresh-btn">
                Refresh List
              </button>
              <button onClick={() => setShowAddForm(true)} className="add-puja-btn">
                ➕ Add New Puja
              </button>
            </div>
          </div>

          <div className="puja-grid">
            {pujas.length === 0 ? (
              <div className="no-data">
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🛕</div>
                <h3>No Pujas Found</h3>
                <p>Start by adding your first puja.</p>
                <button onClick={() => setShowAddForm(true)} className="add-puja-btn">
                  ➕ Add Your First Puja
                </button>
              </div>
            ) : (
              pujas.map((puja) => (
                <div
                  key={puja.id}
                  className="puja-card"
                  onClick={() => handlePujaClick(puja)}
                >
                  <div className="puja-card-header">
                    <h3>{puja.name}</h3>
                  </div>
                  {puja.description && (
                    <p className="puja-description">{puja.description}</p>
                  )}
                  {puja.image_url && (
                    <img
                      src={puja.image_url}
                      alt={puja.name}
                      className="puja-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="puja-card-footer">
                    <span className="view-details">Click to view details →</span>
                  </div>
                  <div className="card-actions">
                    <button
                      onClick={(e) => handleEdit(e, puja)}
                      className="card-edit-btn"
                      title="Edit Puja"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, puja.id)}
                      className="card-delete-btn"
                      title="Delete Puja"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <>
          <div className="puja-detail-header">
            <button onClick={handleBackToList} className="back-btn">
              ← Back to List
            </button>
            <h2>{selectedPuja?.name}</h2>
            <div className="detail-header-actions">
              <button
                onClick={(e) => {
                  handleEdit(e, selectedPuja);
                }}
                className="card-edit-btn"
              >
                ✏️ Edit Puja
              </button>
            </div>
          </div>

          <div className="puja-detail">
            {selectedPuja?.description && (
              <div className="detail-section">
                <h3>📝 Description</h3>
                <p>{selectedPuja.description}</p>
              </div>
            )}

            {selectedPuja?.items && selectedPuja.items.length > 0 && (
              <div className="detail-section">
                <h3>🧺 Required Samagri (Items)</h3>
                <div className="items-list">
                  {selectedPuja.items.map((item, index) => (
                    <div key={index} className="item-card">
                      <span className="item-name">{item.name}</span>
                      {item.quantity && (
                        <span className="item-quantity">({item.quantity})</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedPuja?.items?.length === 0 && (
              <div className="detail-section">
                <h3>🧺 Required Samagri (Items)</h3>
                <p style={{ color: '#888' }}>No items added yet. Click "Edit Puja" to add samagri items.</p>
              </div>
            )}

            {selectedPuja?.image_url && (
              <div className="detail-section">
                <h3>🖼️ Image</h3>
                <img
                  src={selectedPuja.image_url}
                  alt={selectedPuja.name}
                  className="puja-detail-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PujaManagement;